import React, { useEffect, useRef } from 'react'
import LeaguesGrid from '../LeaguesGrid'
import useKeyboardNavigation from '../../../hooks/useKeyboardNavigation'

// Animation delay to allow league selection animation to complete before scrolling
// Matches slideInFromTop animation duration (0.4s) in Register.css
// Reduced slightly to begin scroll before animation completes for smoother UX
const SELECTION_ANIMATION_DELAY_MS = 300

// Mobile breakpoint for responsive behavior (matches CSS media query @media (max-width: 768px))
const MOBILE_BREAKPOINT_PX = 768

function SelectLevelStep({
  leagues,
  selectedLeagueId,
  onSelectLeague,
  isLeagueRegistered,
  onNext,
}) {
  const { onRadioKeyDown, radiogroupRef } = useKeyboardNavigation(
    leagues,
    selectedLeagueId,
    onSelectLeague,
    (league) => !isLeagueRegistered(league.id),
  )

  const selectedLeague = selectedLeagueId
    ? leagues.find((l) => l.id === parseInt(selectedLeagueId, 10)) || null
    : null

  const canProceed = selectedLeague && !isLeagueRegistered(selectedLeagueId)

  const navigationRef = useRef(null)

  // Scroll to navigation button when a league is selected on mobile
  useEffect(() => {
    if (
      selectedLeague &&
      navigationRef.current &&
      window.innerWidth <= MOBILE_BREAKPOINT_PX
    ) {
      // Delay scroll to allow selection animation to complete
      setTimeout(() => {
        navigationRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }, SELECTION_ANIMATION_DELAY_MS)
    }
  }, [selectedLeague])

  return (
    <div className="registration-step">
      <h4 className="step-title">Step 1: Choose Your League</h4>
      <p className="step-description">
        Select the league that matches your skill level and schedule.
      </p>

      <LeaguesGrid
        leagues={leagues}
        selectedLeagueId={selectedLeagueId}
        isLeagueRegistered={isLeagueRegistered}
        onSelect={onSelectLeague}
        onRadioKeyDown={onRadioKeyDown}
        radiogroupRef={radiogroupRef}
      />

      <div className="sr-only" aria-live="polite" role="status">
        {selectedLeague
          ? `Selected: ${selectedLeague.name} • ${selectedLeague.league_type}. Scroll down to continue.`
          : ''}
      </div>

      {/* Mobile helper text when league is selected */}
      {selectedLeague && (
        <div className="league-selected-helper" aria-hidden="true">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="text-success fw-bold">✓</span>
            <span className="fw-semibold text-dark">{selectedLeague.name}</span>
          </div>
          <p className="mb-0 text-muted small">
            <i className="bi bi-arrow-down me-1"></i>
            Click "Continue" below to proceed
          </p>
        </div>
      )}

      <div className="step-navigation" ref={navigationRef}>
        <button
          className="btn btn-primary btn-lg w-100"
          onClick={onNext}
          disabled={!canProceed}
          aria-label="Continue to skill level selection"
        >
          {canProceed ? 'Continue' : 'Select a League to Continue'}
        </button>
      </div>
    </div>
  )
}

export default SelectLevelStep
